/**
 * Procedural top-down Rosie sprite, modeled on the real Tailos Rosie 2.0:
 *   - Rectangular charcoal body (real dims ~12.7" wide × 20.7" long)
 *   - Mint/teal bumper wrapping the rear
 *   - Circular touchscreen "face" near the front
 *   - LiDAR dome on the back deck
 *   - Side wheels poking out
 *
 * Drawn pointing in +X (heading 0) so callers can rotate freely.
 * `accent` overrides the mint bumper so each AI Rosie can have its own color.
 */

const BODY_W = 28; // along heading axis (long side)
const BODY_H = 18; // perpendicular
const BUMPER_TAIL = 5; // mint bumper that wraps the rear

export interface RosieDrawOpts {
  accent: string;
  bodyColor?: string;
  screenColor?: string;
  isPlayer?: boolean;
  isDamaged?: boolean;
  /** Robot Mode — adds a pulsing electric aura. `now` controls the pulse phase. */
  isInvincible?: boolean;
  now?: number;
}

export function drawRosie(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  heading: number,
  scale: number,
  opts: RosieDrawOpts,
): void {
  const body = opts.bodyColor ?? "#1a1f26";
  const screen = opts.screenColor ?? "#0b2a35";
  const accent = opts.accent;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(heading);
  ctx.scale(scale, scale);

  // soft drop shadow
  ctx.save();
  ctx.translate(0.8, 1.4);
  ctx.fillStyle = "rgba(0,0,0,0.28)";
  roundRect(ctx, -BODY_W / 2, -BODY_H / 2, BODY_W, BODY_H, 4);
  ctx.fill();
  ctx.restore();

  // mint rear bumper (drawn first, body sits on top)
  ctx.fillStyle = accent;
  roundRect(
    ctx,
    -BODY_W / 2 - 1,
    -BODY_H / 2 - 1,
    BODY_W + 2,
    BODY_H + 2,
    5,
  );
  ctx.fill();

  // main charcoal body — slightly inset from bumper, with bumper showing at rear
  ctx.fillStyle = body;
  roundRect(
    ctx,
    -BODY_W / 2 + BUMPER_TAIL,
    -BODY_H / 2 + 1.2,
    BODY_W - BUMPER_TAIL - 0.5,
    BODY_H - 2.4,
    3,
  );
  ctx.fill();

  // wheels poking out the sides
  ctx.fillStyle = "#0a0c10";
  ctx.fillRect(-1, -BODY_H / 2 - 0.6, 5, 1.2);
  ctx.fillRect(-1, BODY_H / 2 - 0.6, 5, 1.2);

  // circular touchscreen "face" — Rosie's signature feature
  ctx.save();
  ctx.translate(BODY_W / 2 - 8, 0);
  // bezel
  ctx.fillStyle = "#0a0c10";
  ctx.beginPath();
  ctx.arc(0, 0, 4.8, 0, Math.PI * 2);
  ctx.fill();
  // screen glow
  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 4);
  gradient.addColorStop(0, opts.isDamaged ? "#ff4d4d" : screen);
  gradient.addColorStop(0.7, opts.isDamaged ? "#7a1818" : "#06151c");
  gradient.addColorStop(1, "#020608");
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(0, 0, 4, 0, Math.PI * 2);
  ctx.fill();
  // mint glyph in center (the Tailos "eye")
  ctx.fillStyle = accent;
  ctx.beginPath();
  ctx.arc(0, 0, 1.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // LiDAR dome on the back deck
  ctx.fillStyle = "#2a3038";
  ctx.beginPath();
  ctx.arc(-BODY_W / 2 + 7, 0, 2.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#0a0c10";
  ctx.beginPath();
  ctx.arc(-BODY_W / 2 + 7, 0, 1.6, 0, Math.PI * 2);
  ctx.fill();

  // accent stripe across the back deck (subtle brand detail)
  ctx.strokeStyle = accent;
  ctx.lineWidth = 0.4;
  ctx.globalAlpha = 0.55;
  ctx.beginPath();
  ctx.moveTo(-BODY_W / 2 + 10, -BODY_H / 2 + 3);
  ctx.lineTo(BODY_W / 2 - 11, -BODY_H / 2 + 3);
  ctx.moveTo(-BODY_W / 2 + 10, BODY_H / 2 - 3);
  ctx.lineTo(BODY_W / 2 - 11, BODY_H / 2 - 3);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // player gets a soft mint halo so they're easy to find
  if (opts.isPlayer && !opts.isInvincible) {
    ctx.globalCompositeOperation = "lighter";
    const halo = ctx.createRadialGradient(0, 0, BODY_W / 2, 0, 0, BODY_W);
    halo.addColorStop(0, "rgba(111,214,200,0.35)");
    halo.addColorStop(1, "rgba(111,214,200,0)");
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(0, 0, BODY_W, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";
  }

  // Robot Mode aura — bright electric pulse, much louder than the regular player halo.
  if (opts.isInvincible) {
    const t = (opts.now ?? 0) / 120;
    const pulse = 0.7 + Math.sin(t) * 0.25;
    ctx.globalCompositeOperation = "lighter";
    const aura = ctx.createRadialGradient(0, 0, BODY_W * 0.4, 0, 0, BODY_W * 1.6 * pulse);
    aura.addColorStop(0, "rgba(180,255,240,0.85)");
    aura.addColorStop(0.5, "rgba(111,214,200,0.5)");
    aura.addColorStop(1, "rgba(111,214,200,0)");
    ctx.fillStyle = aura;
    ctx.beginPath();
    ctx.arc(0, 0, BODY_W * 1.6 * pulse, 0, Math.PI * 2);
    ctx.fill();
    // crisp inner ring
    ctx.strokeStyle = `rgba(220,255,250,${0.6 + Math.sin(t) * 0.2})`;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.arc(0, 0, BODY_W * 0.7, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalCompositeOperation = "source-over";
  }

  ctx.restore();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
